import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  MdPhotoLibrary,
  MdOutlineZoomOutMap,
  MdOutlineHeight,
} from "react-icons/md";
import { FiSearch, FiBell, FiHeart, FiMapPin } from "react-icons/fi";
import { FaMoon, FaUserFriends, FaBriefcase } from "react-icons/fa";
import {
  IoIdCardOutline,
  IoPeopleOutline,
  IoSchoolOutline,
} from "react-icons/io5";
import { GoHeartFill } from "react-icons/go";
import { FaSliders } from "react-icons/fa6";
import { RiExpandHorizontalSFill } from "react-icons/ri";
import Filter from "../../components/Filter";
import { useDashboardContext } from "../../context/Dashboard_context";
import "./HomePage.css";
import Loader from "../../components/Loader";
import { getAgeFromDob } from "../../utils/helper";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";

// ─── ProfileCard ──────────────────────────────────────────────────────────────
const ProfileCard = ({ profile, onLike, liked, onView }) => {
  const [imgLoaded, setImgLoaded] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [swiperRef, setSwiperRef] = useState(null);

  const allImages = [
    ...(profile?.photo_url
      ? [{ id: "main", image_url: profile.photo_url }]
      : []),

    ...(profile?.other_images || []).filter(
      (img) => img?.image_url && img.image_url !== profile.photo_url,
    ),
  ];

  const openLightbox = () => {
    setLightboxOpen(true);
    window.history.pushState({ lightbox: true }, "");
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  useEffect(() => {
    const handlePop = () => {
      if (lightboxOpen) setLightboxOpen(false);
    };
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, [lightboxOpen]);

  return (
    <>
      <div className="hp-profile-card">
        <div className="hp-photo-wrapper">
          <div className="hp-zoom-hint">
            <MdOutlineZoomOutMap size={14} />
            <span>Tap to zoom</span>
          </div>
          {allImages.length > 1 && (
            <div className="hp-img-counter">
              <MdPhotoLibrary size={14} />
              <span>
                {currentIdx + 1}/{allImages.length}
              </span>
            </div>
          )}

          <Swiper
            onSwiper={setSwiperRef}
            onSlideChange={(swiper) => setCurrentIdx(swiper.activeIndex)}
            onTap={(swiper) => {
              openLightbox();
            }}
            spaceBetween={0}
            slidesPerView={1}
            resistance={true}
            resistanceRatio={0}
            observer={true}
            observeParents={true}
            style={{ height: "100%" }}
          >
            {allImages.map((img, i) => (
              <SwiperSlide key={img.id || i}>
                <div className="image-container">
                  {!imgLoaded[i] && (
                    <div className="image-loader">
                      <div className="image-loader-animation" />
                    </div>
                  )}
                  <img
                    src={img.image_url}
                    alt={`${profile.name} ${i + 1}`}
                    onLoad={() =>
                      setImgLoaded((prev) => ({ ...prev, [i]: true }))
                    }
                    className={`image ${imgLoaded[i] ? "loaded" : ""}`}
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          {allImages.length > 1 && (
            <div className="dp-slider-dots">
              {allImages.map((_, i) => (
                <span
                  key={i}
                  className={`dp-dot ${currentIdx === i ? "active" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    swiperRef?.slideTo(i);
                  }}
                />
              ))}
            </div>
          )}

          {/* Like button */}
          <button
            className={`hp-like-btn ${liked ? "liked" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              onLike();
            }}
            aria-label="Like"
          >
            {liked ? <GoHeartFill size={18} /> : <FiHeart size={18} />}
          </button>
        </div>

        {/* Card body — click navigates to detail */}
        <div className="hp-card-body" onClick={() => onView(profile)}>
          <div className="hp-name-row">
            <span className="hp-name">{profile.name}</span>
            <span className="hp-age">{getAgeFromDob(profile.dob)} yrs</span>
          </div>
          <div className="hp-profile-id">
            <IoIdCardOutline size={12} /> #{profile.unique_id}
          </div>
          <div className="hp-stats-row">
            <div className="hp-stat-box height">
              <span className="hp-stat-icon">
                <MdOutlineHeight size={20} color="#0284c7" />
              </span>
              <span className="hp-stat-label">Height</span>
              <span className="hp-stat-value">{profile.height}</span>
            </div>
            <div className="hp-stat-box weight">
              <span className="hp-stat-icon">
                <RiExpandHorizontalSFill size={16} color="#d97706" />
              </span>
              <span className="hp-stat-label">Weight</span>
              <span className="hp-stat-value">{profile.weight} KG</span>
            </div>
            <div className="hp-stat-box education">
              <span className="hp-stat-icon">
                <IoSchoolOutline size={16} color="#db2777" />
              </span>
              <span className="hp-stat-label">Education</span>
              <span className="hp-stat-value">
                {profile.education?.name || profile.education}
              </span>
            </div>
          </div>
          <ul className="hp-info-list">
            <li className="hp-info-item">
              <FaBriefcase className="hp-info-icon profession" size={16} />
              <span>{profile.job}</span>
            </li>
            <li className="hp-info-item">
              <FiMapPin className="hp-info-icon location" size={16} />
              <span>
                {profile.city?.name}, {profile.state?.name}
              </span>
            </li>
            <li className="hp-info-item">
              <FaMoon className="hp-info-icon religion" size={14} />
              <span>{profile.religion?.name || "N/A"}</span>
            </li>
            <li className="hp-info-item">
              <FaUserFriends className="hp-info-icon marital" size={14} />
              <span>{profile.marital_status?.name || "N/A"}</span>
            </li>
          </ul>
        </div>
      </div>
      {lightboxOpen && (
        <Lightbox
          open={lightboxOpen}
          close={closeLightbox}
          index={currentIdx}
          slides={allImages.map((img) => ({ src: img.image_url }))}
          plugins={[Zoom]}
          render={{ buttonZoom: () => null }}
        />
      )}
    </>
  );
};
// ─── HomePage ──────────────────────────────────────────────────
const HomePage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});

  const {
    dashboard_Data,
    dashboard_loading,
    GetformList,
    SelectPerson,
    setSelectedProfile,
  } = useDashboardContext();

  useEffect(() => {
    GetformList(new FormData());
  }, []); // eslint-disable-line

  const openProfile = (profile) => {
    localStorage.removeItem("selected_profile");
    localStorage.setItem("selected_profile", JSON.stringify(profile));
    setSelectedProfile(profile);
    navigate(`/details/${profile.unique_id}`, {
      state: { profile },
    });
  };

  const handleLike = (profile) => {
    const formdata = new FormData();
    formdata.append("form_id", profile.id);
    formdata.append("is_select", profile?.is_select === 1 ? 0 : 1);
    SelectPerson(formdata);
  };

  const handleApplyFilters = (filters) => {
    setActiveFilters(filters);

    const formdata = new FormData();
    if (filters.caste) formdata.append("cast_id", filters.caste);
    if (filters.country) formdata.append("country_id", filters.country);
    if (filters.state) formdata.append("state_id", filters.state);
    if (filters.city) formdata.append("city_id", filters.city);
    if (filters.maritalStatus)
      formdata.append("marital_status_id", filters.maritalStatus);

    GetformList(formdata, navigate);
  };

  const handleResetFilters = () => {
    setActiveFilters({});
    GetformList(new FormData(), navigate);
  };

  const filterCount =
    (activeFilters.ageMin || activeFilters.ageMax ? 1 : 0) +
    (activeFilters.caste ? 1 : 0) +
    (activeFilters.maritalStatus ? 1 : 0) +
    (activeFilters.country ? 1 : 0) +
    (activeFilters.state ? 1 : 0) +
    (activeFilters.city ? 1 : 0);

  const filtered = dashboard_Data.filter((profile) => {
    const matchesSearch = profile?.name
      ?.toLowerCase()
      .includes(search.toLowerCase());

    const age = getAgeFromDob(profile.dob);
    let matchesAge = true;
    if (activeFilters.ageMin && age < Number(activeFilters.ageMin))
      matchesAge = false;
    if (activeFilters.ageMax && age > Number(activeFilters.ageMax))
      matchesAge = false;

    return matchesSearch && matchesAge;
  });

  return (
    <>
      {/* ========== HEADER ==========  */}
      <header className="hp-header">
        <span className="hp-header-title">Find Your Match</span>
        <div>
          <button className="hp-bell-btn">
            <FiBell size={25} />
          </button>
        </div>
      </header>
      {/* ========== SEARCH ========== */}
      <div className="hp-search-section">
        <div className="hp-search-wrapper">
          <FiSearch className="hp-search-icon" />
          <input
            className="hp-search-input"
            type="text"
            placeholder="Search by name, location..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {/* ========== FILTER-BTN ========== */}
          <button
            className="hp-filter-btn"
            onClick={() => setFilterOpen(true)}
            aria-label="Open filters"
          >
            <FaSliders size={16} />
            {filterCount > 0 && (
              <span className="hp-filter-badge ">{filterCount}</span>
            )}
          </button>
        </div>
      </div>
      {dashboard_loading ? (
        <Loader />
      ) : (
        <main className="hp-main">
          <div className="hp-section-header">
            <span className="hp-section-title">Suggested Matches</span>
            <span className="hp-results-count">
              {filtered.length} profiles found
            </span>
          </div>
          <div className="hp-cards-grid">
            {filtered.length > 0 ? (
              filtered.map((profile) => (
                <ProfileCard
                  key={profile.id}
                  profile={profile}
                  liked={profile.is_select === 1}
                  onLike={() => handleLike(profile)}
                  onView={openProfile}
                />
              ))
            ) : (
              <div className="hp-empty-state">
                <div className="hp-empty-icon-wrap">
                  <IoPeopleOutline className="hp-empty-icon" />
                </div>
                <h3 className="hp-empty-title"> No profiles found</h3>
                <p className="hp-empty-subtitle">Try adjusting your filters</p>
              </div>
            )}
          </div>
        </main>
      )}
      <Filter
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />
    </>
  );
};

export default HomePage;
